<script setup>
import { ref, computed, onMounted } from "vue";
import { useDateFilter } from "~/composables/useDateFilter";
import { useBalance } from "~/composables/useBalance";
import { useAuth } from "~/composables/useAuth";

definePageMeta({
  layout: "mypage",
});

// ===== Composables =====
const {
  dateOptions: options,
  selectedType,
  selectRef,
  handleSelectChange,
  applyDateFilter,
} = useDateFilter();

const { deposit, withdraw, getTransactionHistory } = useBalance();
const { currentUser } = useAuth();

// ===== 상태 관리 =====
// 보유 머니 (currentUser에서 가져옴)
const balance = computed(() => currentUser.value?.balance || 0);
// 출금 입력 필드에 입력된 값 (문자열)
const withdrawAmount = ref("");
// 출금 가능 금액 (보유 머니와 동일)
const maxWithdrawAmount = computed(() => balance.value);
// 활성화된 필터 ('all' | 'deposit' | 'withdraw')
const activeFilter = ref("all");
// 로딩 상태
const isLoading = ref(false);

// ===== 데이터 =====
const transactionHistory = ref([]);

// API 호출 함수
const loadTransactionHistory = async () => {
  isLoading.value = true;
  try {
    const result = await getTransactionHistory();
    if (result.success && result.history) {
      transactionHistory.value = result.history.map(item => ({
        id: item.id,
        date: item.date,
        type: item.type,
        logType: item.type === 'deposit' ? 'up' : 'down',
        amount: item.amount,
        balance: item.balance,
      }));
    }
  } catch (error) {
    console.error('거래 내역 로드 실패:', error);
  } finally {
    isLoading.value = false;
  }
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(() => {
  loadTransactionHistory();
});

// ===== 계산된 값 =====
// 필터링된 입/출금 내역 (타입 필터 + 날짜 필터 적용)
const filteredTransactionHistory = computed(() => {
  let filtered = transactionHistory.value;

  // 타입 필터링 (전체/입금/출금)
  if (activeFilter.value !== "all") {
    filtered = filtered.filter(
      (transaction) => transaction.type === activeFilter.value
    );
  }

  // 날짜 필터링 적용
  filtered = applyDateFilter(filtered);

  return filtered;
});

// ===== 함수 =====
// 원화 포맷팅 함수 (천 단위 콤마 추가)
const formatKRW = (amount) => {
  return amount.toLocaleString("ko-KR");
};

// 필터 버튼 클릭 핸들러 (전체/입금/출금)
const handleFilterClick = (filter) => {
  activeFilter.value = filter;
};

// 출금 금액 입력 핸들러 (원화만 - 정수만 입력 가능, 최대값 제한, 콤마 자동 추가)
const handleWithdrawInput = (event) => {
  let value = event.target.value;
  // 콤마 제거하고 숫자만 추출
  value = value.replace(/[^\d]/g, "");
  let numValue = parseInt(value) || 0;

  // 보유 머니를 초과하지 않도록 제한
  if (numValue > maxWithdrawAmount.value) {
    numValue = maxWithdrawAmount.value;
  }

  // 콤마 포맷팅된 문자열로 변환
  const formattedValue = formatKRW(numValue);
  withdrawAmount.value = numValue.toString();
  event.target.value = formattedValue;
};

// ===== 충전 기능 사용 안 함 (주석처리) =====
// 충전 핸들러
// const handleDeposit = async () => {
//   const amount = prompt('충전할 금액을 입력하세요 (최소 10,000원):');
//   if (!amount) return;

//   const numAmount = parseInt(amount.replace(/,/g, ''));
//   if (isNaN(numAmount) || numAmount < 10000) {
//     alert('최소 충전 금액은 10,000원입니다.');
//     return;
//   }

//   isLoading.value = true;
//   try {
//     const result = await deposit(numAmount);
//     if (result.success) {
//       alert(result.message);
//       // 잔액 업데이트
//       if (currentUser.value) {
//         currentUser.value.balance = result.balance;
//       }
//       // 거래 내역 새로고침
//       await loadTransactionHistory();
//     } else {
//       alert(result.message);
//     }
//   } catch (error) {
//     alert('충전 중 오류가 발생했습니다.');
//   } finally {
//     isLoading.value = false;
//   }
// };

// 출금 핸들러
const handleWithdrawRequest = async () => {
  const amount = parseInt(withdrawAmount.value);
  if (!amount || amount < 10000) {
    alert('최소 출금 금액은 10,000원입니다.');
    return;
  }

  if (amount > maxWithdrawAmount.value) {
    alert('출금 가능 금액을 초과했습니다.');
    return;
  }

  if (!confirm(`${formatKRW(amount)}원을 출금하시겠습니까?`)) {
    return;
  }

  isLoading.value = true;
  try {
    const result = await withdraw(amount);
    if (result.success) {
      alert(result.message);
      // 잔액 업데이트
      if (currentUser.value) {
        currentUser.value.balance = result.balance;
      }
      // 출금 입력 초기화
      withdrawAmount.value = '';
      // 거래 내역 새로고침
      await loadTransactionHistory();
    } else {
      alert(result.message);
    }
  } catch (error) {
    alert('출금 중 오류가 발생했습니다.');
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div class="mypage__wallet">
    <!-- ===== 보유 머니 영역 ===== -->
    <div class="mypage__wallet-item">
      <strong class="mypage__wallet-title">내 보유 머니</strong>
      <div class="mypage__wallet-balance">
        <img src="/images/add_funds.svg" alt="₩" />
        <strong class="mypage__wallet-amount mypage__wallet-amount--sm"
          >{{ formatKRW(balance) }}원</strong
        >
      </div>
      <div class="mypage__wallet-form-info">
        <p>
          <span>입출금은 캐시파트너에서 입금과 출금이 가능합니다.</span>
        </p>
        <!-- 충전 기능 사용 안 함 (주석처리) -->
        <!-- <button type="button" class="btn-primary btn-block" @click="handleDeposit" :disabled="isLoading">충전하기</button> -->
      </div>
    </div>

    <!-- ===== 출금 영역 ===== -->
    <div class="mypage__wallet-item">
      <strong class="mypage__wallet-title">출금액</strong>
      <div class="mypage__wallet-form">
        <input
          type="text"
          class="input-text input-md"
          placeholder="0"
          :value="
            withdrawAmount ? formatKRW(parseInt(withdrawAmount) || 0) : ''
          "
          @input="handleWithdrawInput"
        />
        <span class="mypage__wallet-form-prefix">원</span>
      </div>
      <div class="mypage__wallet-form-info">
        <p>
          <span>출금가능:</span>
          <strong>₩{{ formatKRW(maxWithdrawAmount) }}</strong>
        </p>
        <button type="button" class="btn-primary btn-block" @click="handleWithdrawRequest" :disabled="isLoading">출금 요청</button>
      </div>
    </div>

    <!-- ===== 입/출금 내역 영역 ===== -->
    <div class="mypage__wallet-item mypage__wallet-item--column">
      <strong class="mypage__wallet-title">입/출금 내역 (USDT)</strong>
      <div class="mypage__wallet-history">
        <!-- ===== 필터 영역 ===== -->
        <div class="mypage__wallet-history-filter">
          <!-- 타입 필터 버튼 (전체/입금/출금) -->
          <div class="mypage__wallet-history-filter-btns">
            <button
              type="button"
              :class="[
                'btn-sm',
                activeFilter === 'all' ? 'btn-primary' : 'btn-ghost',
              ]"
              @click="handleFilterClick('all')"
            >
              전체
            </button>
            <button
              type="button"
              :class="[
                'btn-sm',
                activeFilter === 'deposit' ? 'btn-primary' : 'btn-ghost',
              ]"
              @click="handleFilterClick('deposit')"
            >
              입금
            </button>
            <button
              type="button"
              :class="[
                'btn-sm',
                activeFilter === 'withdraw' ? 'btn-primary' : 'btn-ghost',
              ]"
              @click="handleFilterClick('withdraw')"
            >
              출금
            </button>
          </div>
          <!-- 날짜 필터 선택 (지난 N일 또는 날짜 범위 선택) -->
          <div class="mypage__wallet-history-filter-select">
            <Select
              ref="selectRef"
              v-model="selectedType"
              :options="options"
              placeholder="전체"
              size="md"
              @change="handleSelectChange"
            />
          </div>
        </div>

        <!-- ===== 테이블 영역 ===== -->
        <div class="table__inner">
          <div class="table__wrap">
            <table class="table table--b">
              <colgroup>
                <col width="180px" />
                <col width="80px" />
                <col width="118px" />
                <col width="167px" />
                <col width="167px" />
              </colgroup>
              <thead class="table__head">
                <tr class="table__row table__row--th">
                  <th class="table__cell table__cell--th">날짜</th>
                  <th class="table__cell table__cell--th">구분</th>
                  <th class="table__cell table__cell--th">로그타입</th>
                  <th class="table__cell table__cell--th">변경금액</th>
                  <th class="table__cell table__cell--th">변경후 잔액</th>
                </tr>
              </thead>
              <tbody class="table__body">
                <tr
                  v-for="transaction in filteredTransactionHistory"
                  :key="transaction.id"
                  class="table__row table__row--td"
                >
                  <!-- 날짜 -->
                  <td class="table__cell table__cell--td">
                    <span class="table__cell-date">{{ transaction.date }}</span>
                  </td>
                  <!-- 구분 (입금/출금, primary/secondary 색상 적용) -->
                  <td
                    class="table__cell table__cell--td"
                    :class="{
                      'table__cell--primary': transaction.type === 'deposit',
                      'table__cell--secondary': transaction.type === 'withdraw',
                    }"
                  >
                    <span>{{
                      transaction.type === "deposit" ? "입금" : "출금"
                    }}</span>
                  </td>
                  <!-- 로그타입 (배팅 승/패/캐시파트너, badge 스타일 적용) -->
                  <td class="table__cell table__cell--td">
                    <span
                      v-if="transaction.logType === 'up'"
                      class="badge badge--up badge--line"
                      >배팅 승</span
                    >
                    <span
                      v-else-if="transaction.logType === 'down'"
                      class="badge badge--down badge--line"
                      >배팅 패</span
                    >
                    <span
                      v-else-if="transaction.logType === 'warning'"
                      class="badge badge--warning badge--line"
                      >캐시파트너</span
                    >
                  </td>
                  <!-- 변경금액 (입금: +, 출금: -, primary/secondary 색상 적용, 원화 포맷) -->
                  <td
                    class="table__cell table__cell--td"
                    :class="{
                      'table__cell--primary': transaction.type === 'deposit',
                      'table__cell--secondary': transaction.type === 'withdraw',
                    }"
                  >
                    <span>
                      {{ transaction.type === "deposit" ? "+" : "-"
                      }}{{ formatKRW(Math.round(transaction.amount)) }}원
                    </span>
                  </td>
                  <!-- 변경후 잔액 (원화 포맷) -->
                  <td class="table__cell table__cell--td">
                    <span
                      >{{ formatKRW(Math.round(transaction.balance)) }}원</span
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style></style>
